import { DynamoDBClient } from "@aws-sdk/client-dynamodb";
import { 
  DynamoDBDocumentClient, 
  PutCommand, 
  GetCommand, 
  UpdateCommand, 
  DeleteCommand, 
  QueryCommand, 
  ScanCommand,
  BatchGetCommand,
  BatchWriteCommand 
} from "@aws-sdk/lib-dynamodb";
import { v4 as uuidv4 } from 'uuid';
import { getCurrentUser, fetchAuthSession } from 'aws-amplify/auth';
import DOMPurify from 'dompurify';
// Update the imports section
import { generateClient } from 'aws-amplify/api';
// Lazy client initialization to ensure Amplify is configured first
let amplifyClient = null;
const getAmplifyClient = () => {
  if (!amplifyClient) {
    amplifyClient = generateClient();
  }
  return amplifyClient;
};

// Initialize DynamoDB client with credentials
let dynamoClient = null;
let docClient = null;

const initializeDynamoDB = async () => {
  if (dynamoClient) return docClient;
  
  try {
    const session = await fetchAuthSession();
    const credentials = session.credentials;
    
    dynamoClient = new DynamoDBClient({
      region: 'ap-south-1',
      credentials: credentials ? {
        accessKeyId: credentials.accessKeyId,
        secretAccessKey: credentials.secretAccessKey,
        sessionToken: credentials.sessionToken
      } : undefined
    });
  } catch (error) {
    dynamoClient = new DynamoDBClient({ region: 'ap-south-1' });
  }
  
  docClient = DynamoDBDocumentClient.from(dynamoClient);
  return docClient;
};

// Dynamic table name resolver using environment variables
let tableNames = null;

const getTableNames = async () => {
  if (tableNames) return tableNames;
  
  try {
    // Force production tables for now
    tableNames = {
      NEWS_TABLE: 'News-aly6uqp67feijkdjvfjhq6vkmm-prod',
      NEWS_BOOKMARKS_TABLE: 'NewsBookmark-aly6uqp67feijkdjvfjhq6vkmm-prod',
      NEWS_VIEWS_TABLE: 'NewsView-aly6uqp67feijkdjvfjhq6vkmm-prod'
    };
    
    console.log('Using table names:', tableNames);
    return tableNames;
  } catch (error) {
    console.warn('Error determining table names:', error.message);
    // Default to prod for safety
    tableNames = {
      NEWS_TABLE: 'News-aly6uqp67feijkdjvfjhq6vkmm-prod',
      NEWS_BOOKMARKS_TABLE: 'NewsBookmark-aly6uqp67feijkdjvfjhq6vkmm-prod',
      NEWS_VIEWS_TABLE: 'NewsView-aly6uqp67feijkdjvfjhq6vkmm-prod'
    };
    return tableNames;
  }
};

// News Operations
export const newsOperations = {
  // Admin operations
  createNews: async (newsData) => {
    try {
      const client = getAmplifyClient();
      
      // Validate required fields
      if (!newsData.title || !newsData.content || !newsData.published_at) {
        return { success: false, error: 'Missing required fields' };
      }
      
      const CREATE_NEWS_MUTATION = `
        mutation CreateNews($input: CreateNewsInput!) {
          createNews(input: $input) {
            id
            title
            content
            published_at
            status
            createdAt
            updatedAt
          }
        }
      `;
      
      const result = await client.graphql({
        query: CREATE_NEWS_MUTATION,
        variables: {
          input: {
            title: newsData.title,
            content: newsData.content,
            description: newsData.description || '',
            source: newsData.source || '',
            source_url: newsData.source_url || '',
            author: newsData.author || '',
            published_at: new Date(newsData.published_at).toISOString(),
            featured_image: newsData.featured_image || '',
            tmdb_id: newsData.tmdb_id || null,
            media_type: newsData.media_type || 'movie',
            category: newsData.category || 'movies',
            tags: newsData.tags || [],
            status: newsData.status || 'draft',
            is_featured: newsData.is_featured || false,
            is_breaking: newsData.is_breaking || false,
            created_by: newsData.created_by || '',
            view_count: 0,
            bookmark_count: 0,
            share_count: 0
          }
        }
      });
      
      return { success: true, data: result.data.createNews };
    } catch (error) {
      console.error('Error creating news article:', error);
      return { success: false, error: error.message };
    }
  },

  updateNews: async (newsId, updates) => {
    try {
      const client = getAmplifyClient();
      
      const UPDATE_NEWS_MUTATION = `
        mutation UpdateNews($input: UpdateNewsInput!) {
          updateNews(input: $input) {
            id
            title
            content
            published_at
            status
            updatedAt
          }
        }
      `;
      
      // Filter out fields not in schema
      const { entity_id, entity_type, entity_name, ...validUpdates } = updates;
      
      const result = await client.graphql({
        query: UPDATE_NEWS_MUTATION,
        variables: {
          input: {
            id: newsId,
            ...validUpdates,
            published_at: validUpdates.published_at ? new Date(validUpdates.published_at).toISOString() : undefined
          }
        }
      });
      
      return { success: true, data: result.data.updateNews };
    } catch (error) {
      console.error('Error updating news:', error);
      return { success: false, error: error.message };
    }
  },

  deleteNews: async (newsId) => {
    try {
      const client = getAmplifyClient();
      
      const DELETE_NEWS_MUTATION = `
        mutation DeleteNews($input: DeleteNewsInput!) {
          deleteNews(input: $input) {
            id
          }
        }
      `;
      
      const result = await client.graphql({
        query: DELETE_NEWS_MUTATION,
        variables: {
          input: { id: newsId }
        }
      });

      return { success: true };
    } catch (error) {
      console.error('Error deleting news:', error);
      return { success: false, error: error.message };
    }
  },

  getNewsById: async (newsId) => {
    try {
      const client = getAmplifyClient();
      
      const GET_NEWS_QUERY = `
        query GetNews($id: ID!) {
          getNews(id: $id) {
            id
            title
            description
            content
            source
            source_url
            author
            published_at
            featured_image
            tmdb_id
            media_type
            category
            tags
            status
            is_featured
            is_breaking
            created_by
            view_count
            bookmark_count
            share_count
            createdAt
            updatedAt
          }
        }
      `;
      
      const result = await client.graphql({
        query: GET_NEWS_QUERY,
        variables: { id: newsId }
      });

      return { success: true, item: result.data.getNews };
    } catch (error) {
      console.error('Error getting news by ID:', error);
      return { success: false, error: error.message };
    }
  },

  getAdminNewsList: async (filters = {}) => {
    try {
      const client = getAmplifyClient();
      const { status = 'all', limit = 50, nextToken = null } = filters;
      
      const LIST_NEWS_QUERY = `
        query ListNews($filter: ModelNewsFilterInput, $limit: Int, $nextToken: String) {
          listNews(filter: $filter, limit: $limit, nextToken: $nextToken) {
            items {
              id
              title
              description
              content
              source
              author
              published_at
              featured_image
              tmdb_id
              category
              status
              is_featured
              is_breaking
              view_count
              bookmark_count
              createdAt
              updatedAt
            }
            nextToken
          }
        }
      `;
      
      const filter = status !== 'all' ? { status: { eq: status } } : null;
      
      const result = await client.graphql({
        query: LIST_NEWS_QUERY,
        variables: { filter, limit, nextToken }
      });
      
      return { 
        success: true, 
        items: result.data.listNews.items || [], 
        nextToken: result.data.listNews.nextToken 
      };
    } catch (error) {
      console.error('Error getting admin news list:', error);
      return { success: false, error: error.message };
    }
  },

  // Public operations
  getPublishedNews: async (filters = {}) => {
    try {
      const client = await initializeDynamoDB();
      const { NEWS_TABLE } = await getTableNames();
      const { limit = 20, lastKey = null } = filters;
      
      const params = {
        TableName: NEWS_TABLE,
        IndexName: 'byCategory',
        KeyConditionExpression: 'category = :category',
        FilterExpression: '#status = :status',
        ExpressionAttributeNames: { '#status': 'status' },
        ExpressionAttributeValues: { 
          ':category': 'movies',
          ':status': 'published'
        },
        ScanIndexForward: false, // Latest first
        Limit: limit
      };

      if (lastKey) {
        params.ExclusiveStartKey = lastKey;
      }

      const result = await client.send(new QueryCommand(params));
      
      return { 
        success: true, 
        items: result.Items || [], 
        lastKey: result.LastEvaluatedKey 
      };
    } catch (error) {
      console.error('Error getting published news:', error);
      return { success: false, error: error.message };
    }
  },

  getMovieNews: async (tmdbId, filters = {}) => {
    try {
      const client = await initializeDynamoDB();
      const { NEWS_TABLE } = await getTableNames();
      const { limit = 10, lastKey = null } = filters;
      
      const params = {
        TableName: NEWS_TABLE,
        IndexName: 'byMovie',
        KeyConditionExpression: 'tmdb_id = :movie',
        FilterExpression: '#status = :status',
        ExpressionAttributeNames: { '#status': 'status' },
        ExpressionAttributeValues: { 
          ':movie': tmdbId,
          ':status': 'published'
        },
        ScanIndexForward: false, // Latest first
        Limit: limit
      };

      if (lastKey) {
        params.ExclusiveStartKey = lastKey;
      }

      const result = await client.send(new QueryCommand(params));
      
      return { 
        success: true, 
        items: result.Items || [], 
        lastKey: result.LastEvaluatedKey 
      };
    } catch (error) {
      console.error('Error getting movie news:', error);
      return { success: false, error: error.message };
    }
  },

  searchNews: async (query, filters = {}) => {
    try {
      const client = await initializeDynamoDB();
      const { NEWS_TABLE } = await getTableNames();
      const { limit = 20, lastKey = null } = filters;
      
      const params = {
        TableName: NEWS_TABLE,
        FilterExpression: '(contains(title, :query) OR contains(description, :query) OR contains(content, :query)) AND #status = :status',
        ExpressionAttributeNames: { '#status': 'status' },
        ExpressionAttributeValues: { 
          ':query': query,
          ':status': 'published'
        },
        Limit: limit
      };

      if (lastKey) {
        params.ExclusiveStartKey = lastKey;
      }

      const result = await client.send(new ScanCommand(params));
      
      return { 
        success: true, 
        items: result.Items || [], 
        lastKey: result.LastEvaluatedKey 
      };
    } catch (error) {
      console.error('Error searching news:', error);
      return { success: false, error: error.message };
    }
  },

  // User operations
  bookmarkNews: async (userId, newsId) => {
    try {
      const client = await initializeDynamoDB();
      const { NEWS_BOOKMARKS_TABLE, NEWS_TABLE } = await getTableNames();
      const now = new Date().toISOString();
      
      await client.send(new PutCommand({
        TableName: NEWS_BOOKMARKS_TABLE,
        Item: {
          id: `${userId}#${newsId}`,
          userID: userId,
          newsID: newsId,
          createdAt: now
        }
      }));

      // Increment bookmark count
      await client.send(new UpdateCommand({
        TableName: NEWS_TABLE,
        Key: { id: newsId },
        UpdateExpression: 'ADD bookmark_count :inc',
        ExpressionAttributeValues: { ':inc': 1 }
      }));

      return { success: true };
    } catch (error) {
      console.error('Error bookmarking news:', error);
      return { success: false, error: error.message };
    }
  },

  removeBookmark: async (userId, newsId) => {
    try {
      const client = await initializeDynamoDB();
      const { NEWS_BOOKMARKS_TABLE, NEWS_TABLE } = await getTableNames();
      await client.send(new DeleteCommand({
        TableName: NEWS_BOOKMARKS_TABLE,
        Key: { id: `${userId}#${newsId}` }
      }));

      // Decrement bookmark count
      await client.send(new UpdateCommand({
        TableName: NEWS_TABLE,
        Key: { id: newsId },
        UpdateExpression: 'ADD bookmark_count :dec',
        ExpressionAttributeValues: { ':dec': -1 }
      }));

      return { success: true };
    } catch (error) {
      console.error('Error removing bookmark:', error);
      return { success: false, error: error.message };
    }
  },

  getUserBookmarks: async (userId, filters = {}) => {
    try {
      const client = await initializeDynamoDB();
      const { NEWS_BOOKMARKS_TABLE } = await getTableNames();
      const { limit = 20, lastKey = null } = filters;
      
      const params = {
        TableName: NEWS_BOOKMARKS_TABLE,
        IndexName: 'byUser',
        KeyConditionExpression: 'userID = :user',
        ExpressionAttributeValues: { ':user': userId },
        ScanIndexForward: false,
        Limit: limit
      };

      if (lastKey) {
        params.ExclusiveStartKey = lastKey;
      }

      const result = await client.send(new QueryCommand(params));
      
      return { 
        success: true, 
        items: result.Items || [], 
        lastKey: result.LastEvaluatedKey 
      };
    } catch (error) {
      console.error('Error getting user bookmarks:', error);
      return { success: false, error: error.message };
    }
  },

  trackNewsView: async (newsId, userId = null) => {
    try {
      const client = await initializeDynamoDB();
      const { NEWS_VIEWS_TABLE, NEWS_TABLE } = await getTableNames();
      const now = new Date().toISOString();
      const timestamp = Date.now();
      
      // Add view record
      await client.send(new PutCommand({
        TableName: NEWS_VIEWS_TABLE,
        Item: {
          id: `${newsId}#${timestamp}#${userId || 'anonymous'}`,
          newsID: newsId,
          userID: userId,
          viewedAt: now,
          ttl: Math.floor(Date.now() / 1000) + (30 * 24 * 60 * 60)
        }
      }));

      // Increment view count
      await client.send(new UpdateCommand({
        TableName: NEWS_TABLE,
        Key: { id: newsId },
        UpdateExpression: 'ADD view_count :inc',
        ExpressionAttributeValues: { ':inc': 1 }
      }));

      return { success: true };
    } catch (error) {
      console.error('Error tracking news view:', error);
      return { success: false, error: error.message };
    }
  },

  // Batch operations
  batchGetNews: async (newsIds) => {
    try {
      const client = await initializeDynamoDB();
      const { NEWS_TABLE } = await getTableNames();
      const keys = newsIds.map(newsId => ({ id: newsId }));
      const chunks = [];
      
      // DynamoDB batch limit is 100 items
      for (let i = 0; i < keys.length; i += 100) {
        chunks.push(keys.slice(i, i + 100));
      }
      
      const results = [];
      for (const chunk of chunks) {
        const result = await client.send(new BatchGetCommand({
          RequestItems: {
            [NEWS_TABLE]: { Keys: chunk }
          }
        }));
        results.push(...(result.Responses[NEWS_TABLE] || []));
      }
      
      return { success: true, items: results };
    } catch (error) {
      console.error('Error batch getting news:', error);
      return { success: false, error: error.message };
    }
  },

  // Content sanitization
  sanitizeContent: (content) => {
    if (typeof window !== 'undefined' && DOMPurify) {
      return DOMPurify.sanitize(content, {
        ALLOWED_TAGS: ['p', 'br', 'strong', 'em', 'u', 'h1', 'h2', 'h3', 'ul', 'ol', 'li', 'a'],
        ALLOWED_ATTR: ['href', 'target']
      });
    }
    return content;
  },

  // Enhanced search with filters
  searchNewsAdvanced: async (query, filters = {}) => {
    try {
      const client = await initializeDynamoDB();
      const { NEWS_TABLE } = await getTableNames();
      const { category, dateFrom, dateTo, limit = 20, lastKey = null } = filters;
      
      let filterExpression = '(contains(title, :query) OR contains(description, :query) OR contains(content, :query)) AND #status = :status';
      const expressionAttributeNames = { '#status': 'status' };
      const expressionAttributeValues = { 
        ':query': query,
        ':status': 'published'
      };
      
      if (category) {
        filterExpression += ' AND category = :category';
        expressionAttributeValues[':category'] = category;
      }
      
      if (dateFrom) {
        filterExpression += ' AND published_at >= :dateFrom';
        expressionAttributeValues[':dateFrom'] = dateFrom;
      }
      
      if (dateTo) {
        filterExpression += ' AND published_at <= :dateTo';
        expressionAttributeValues[':dateTo'] = dateTo;
      }

      const params = {
        TableName: NEWS_TABLE,
        FilterExpression: filterExpression,
        ExpressionAttributeNames: expressionAttributeNames,
        ExpressionAttributeValues: expressionAttributeValues,
        Limit: limit
      };

      if (lastKey) {
        params.ExclusiveStartKey = lastKey;
      }

      const result = await client.send(new ScanCommand(params));
      
      return { 
        success: true, 
        items: result.Items || [], 
        lastKey: result.LastEvaluatedKey 
      };
    } catch (error) {
      console.error('Error searching news:', error);
      return { success: false, error: error.message };
    }
  },

  // Retry mechanism
  withRetry: async (operation, maxRetries = 3) => {
    for (let i = 0; i < maxRetries; i++) {
      try {
        return await operation();
      } catch (error) {
        if (i === maxRetries - 1) throw error;
        await new Promise(resolve => setTimeout(resolve, Math.pow(2, i) * 1000));
      }
    }
  }
};

export default newsOperations;